import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useVertical } from '../../context/VerticalContext';
import { lookupVrm, VrmLookupError } from '../../services/vrmService';
/**
 * One place deciding what the user is told when a reg lookup fails.
 * "Lookup unavailable" for a reg that simply doesn't exist blames our systems
 * for the user's typo, and sends them looking for a problem that isn't there.
 */
const vrmFailureMessage = (err: unknown): string => {
    const kind = err instanceof VrmLookupError ? err.kind : 'unavailable';
    switch (kind) {
        case 'invalid_format':
            return "That doesn't look like a UK registration — check it and try again.";
        case 'not_found':
            return 'No vehicle found with that registration. Check it, or enter the details by hand.';
        case 'rate_limited':
            return 'The lookup service is busy. Try again in a moment, or enter the details by hand.';
        default:
            return 'Lookup unavailable — enter the details by hand.';
    }
};

import type { VehicleDetails } from '../../services/vrmService';
import { ArrowLeft, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { reportError } from '../../lib/errors';
import { strike } from '../../lib/haptics';
import { motIsValid, taxIsValid } from '../../lib/vehicle';

export interface FinalVehicleData extends VehicleDetails {
  vrm: string;
  model: string;
  id?: string;
  // DB-specific fields that may come back from local lookup
  year?: string | number | null;
  engine?: string | null;
  mileage?: string | number | null;
  owner_id?: string | null;
  fuel_type?: string | null;
  // The last MOT reading, as stored on a vehicle already on file (a fresh
  // lookup carries it in motHistory instead). See motOdometerOffer.
  mot_last_odometer_value?: string | null;
  mot_last_odometer_unit?: string | null;
  mot_last_test_date?: string | null;
  source?: 'dvla' | 'db' | 'manual';
}

export interface LinkedCustomerData {
  id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
}

interface VRMLookupProps {
  onVehicleConfirmed: (vehicle: FinalVehicleData) => void;
  /** Invalidate the host's selected vehicle when starting a replacement lookup. */
  onVehicleCleared?: () => void;
  onCustomerFound?: (customer: LinkedCustomerData) => void;
  // Receives whatever VRM the user typed so the parent can pre-fill its manual entry.
  // The Skip → Enter Manually fallback is only relevant when an external lookup fails;
  // without it the typed VRM would be lost when the user bails out of the lookup.
  onSkipToManual?: (vrm: string) => void;
  /**
   * Fired with the vehicle this lookup has RESOLVED BUT THE USER HAS NOT YET
   * CONFIRMED, and with null whenever there is no longer one.
   *
   * It exists because of a silent data loss on the Add Customer screen. The
   * lookup succeeded, the DVLA card was on screen — make, year, MOT, mileage,
   * advisories, all of it — and the user tapped the screen's own "Create
   * Customer Profile" button in the sticky footer instead of the "Add This
   * Vehicle" button inside the card, which on a phone is below the fold. This
   * component had told the parent nothing, so the customer was created with no
   * vehicle at all and no warning that anything had been dropped.
   *
   * A host screen with its own submit button MUST take this and adopt it on
   * submit. See AddCustomerModal.
   */
  onPendingVehicleChange?: (vehicle: FinalVehicleData | null) => void;
  className?: string;
  confirmButtonLabel?: string; // e.g., "Apply to Job", "Confirm Vehicle", etc.
  mileageHint?: string;
}

export function VRMLookup({
  onVehicleConfirmed,
  onVehicleCleared,
  onCustomerFound,
  onSkipToManual,
  onPendingVehicleChange,
  className = '',
  confirmButtonLabel = 'Confirm Vehicle',
  mileageHint,
}: VRMLookupProps) {
  const { activeOrgId } = useVertical();
  const [vrmInput, setVrmInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<'db' | 'dvla' | null>(null);
  // Drives only the colour of the error box (amber "our fault" vs red "check
  // what you typed"). It must NOT gate the manual-entry escape hatch: a reg
  // DVLA has never heard of — an import, a trailer, a typo the user is sure
  // about — left the user with no way forward at all.
  const [apiUnavailable, setApiUnavailable] = useState(false);

  // State for post-DVLA-lookup (awaiting model)
  const [dvlaData, setDvlaData] = useState<VehicleDetails | null>(null);
  const [modelInput, setModelInput] = useState('');
  const [appliedVehicle, setAppliedVehicle] = useState<FinalVehicleData | null>(null);

  // The parent is kept in step with what is on screen, so a submit from
  // outside this component can never quietly drop a resolved vehicle.
  const emitPending = (data: VehicleDetails | null, model: string, vrm: string) => {
    if (!onPendingVehicleChange) return;
    if (!data) { onPendingVehicleChange(null); return; }
    onPendingVehicleChange({
      ...data,
      vrm,
      model: model.trim(),
      source: 'dvla',
    });
  };

  // Each lookup re-mounts the bolt, which replays its one-shot animation.
  const [boltShot, setBoltShot] = useState(0);

  const handleLookup = async () => {
    const sanitizedInput = vrmInput.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!sanitizedInput) {
      setError('Please enter a valid VRM.');
      return;
    }

    setVrmInput(sanitizedInput);
    // The gold bolt and the strike play alongside the request, never before it.
    setBoltShot((n) => n + 1);
    strike('tap');
    setLoading(true);
    setError(null);
    setDvlaData(null);
    setModelInput('');
    setSource(null);
    emitPending(null, '', sanitizedInput);

    try {
      // === STEP 1: Check local Supabase database FIRST (scoped to this org) ===
      let localVehicle = null;
      if (activeOrgId) {
        const { data, error: dbError } = await supabase
          .from('vehicles')
          .select('*')
          .eq('vrm', sanitizedInput)
          .eq('org_id', activeOrgId)
          .maybeSingle();

        if (dbError) {
          console.warn('Local DB lookup warning:', dbError);
        } else {
          localVehicle = data;
        }
      }

      if (localVehicle && localVehicle.model) {
        // Found locally with a complete record — emit immediately
        setSource('db');
        const confirmed = { ...localVehicle, source: 'db' } as FinalVehicleData;
        setAppliedVehicle(confirmed);
        onVehicleConfirmed(confirmed);

        // Also look up linked customer if callback is provided.
        //
        // Only full_name comes off the join: customers.phone and customers.email
        // are the write interface, encrypted by a BEFORE trigger and emptied on
        // disk, so embedding them handed every caller a customer whose contact
        // details were silently NULL. Downstream that reads as "this customer
        // has no email" — and, worse, prompts the user to re-type details they
        // already had, which is how duplicate customer records got created.
        if (onCustomerFound && localVehicle.id) {
          const { data: jobInfo, error: jobInfoError } = await supabase
            .from('jobs')
            .select('customer_id, customers:customers!inner(id, full_name)')
            .eq('vehicle_id', localVehicle.id)
            .eq('org_id', activeOrgId!)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (jobInfoError) reportError(jobInfoError, 'vrm-lookup.jobs');

          const joined = jobInfo?.customers as unknown as { id: string; full_name: string } | null;
          if (joined) {
            const { data: contact, error: contactError } = await supabase
              .from('customers_decrypted')
              .select('email, phone')
              .eq('id', joined.id)
              .maybeSingle();
            if (contactError) reportError(contactError, 'vrm-lookup.customers-decrypted');

            onCustomerFound({
              id: joined.id,
              full_name: joined.full_name,
              phone: contact?.phone ?? null,
              email: contact?.email ?? null,
            });
          }
        }

        setLoading(false);
        return;
      }

      // === STEP 2: Fall back to the government lookup (DVLA + DVSA) ===
      setSource('dvla');
      const data = await lookupVrm(sanitizedInput);
      setDvlaData(data);
      // DVSA's MOT History API returns the model; DVLA never has. When it
      // answers, the one field the user always had to type is already filled
      // and they just confirm. When it doesn't, the box is empty as before.
      setModelInput(data.model ?? '');
      emitPending(data, data.model ?? '', sanitizedInput);

    } catch (err) {
      // A reg that doesn't exist is still a working lookup, so don't flip into
      // the "API is down" state — the user just needs to check what they typed.
      // vrmService already logs the genuine faults; re-logging every miss here
      // just doubled the console noise for an expected outcome.
      const kind = err instanceof VrmLookupError ? err.kind : 'unavailable';
      if (!(err instanceof VrmLookupError)) console.error(err);
      setApiUnavailable(kind === 'unavailable' || kind === 'rate_limited');
      setError(vrmFailureMessage(err));
      emitPending(null, '', sanitizedInput);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmNewVehicle = () => {
    if (!dvlaData) return;

    const sanitizedModel = modelInput.trim();
    if (!sanitizedModel) {
      setError('Vehicle Model is required.');
      return;
    }

    const finalData: FinalVehicleData = {
      ...dvlaData,
      vrm: vrmInput,
      model: sanitizedModel,
      source: 'dvla',
    };

    emitPending(null, '', vrmInput);
    setAppliedVehicle(finalData);
    onVehicleConfirmed(finalData);
  };

  // This component contains NO <form> element, and must not grow one.
  //
  // It is embedded inside screens that are themselves a form — Add Customer is
  // one — and a <form> nested in a <form> is invalid HTML. On the Clients page
  // that nesting is what turned "LOOKUP" into a real GET submit: the page
  // reloaded and the reg reappeared in the address bar as
  // /clients?vrm=NA14VOP, taking every field the user had typed with it.
  //
  // So both actions here are plain type="button" + onClick, and Enter in either
  // text field is handled explicitly and stopped from bubbling. There is then
  // no default submission for a host screen to inherit, whatever it wraps this
  // in.
  if (appliedVehicle) {
    return (
      <div className={`flex items-center justify-between gap-3 rounded-lg border border-line bg-tint/5 p-3 ${className}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="fd-plate">{appliedVehicle.vrm}</span>
            <span className="text-xs text-success inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Applied</span>
          </div>
          <p className="mt-1 text-sm text-fg-2 break-words">{[appliedVehicle.make, appliedVehicle.model].filter(Boolean).join(' ')}</p>
        </div>
        <button type="button" className="fd-btn shrink-0" disabled={loading} onClick={() => {
          setAppliedVehicle(null);
          setDvlaData(null);
          setError(null);
          setSource(null);
          emitPending(null, '', vrmInput);
          onVehicleCleared?.();
        }}>Change vehicle</button>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>

      {/* 1. NUMBER PLATE LOOKUP INPUT */}
      <div className="relative">
        <label htmlFor="vrm-lookup-input" className="sr-only">Vehicle Registration Mark</label>
        {/* Drawn as a real UK plate — plate yellow, GB blue band, black
            characters — because that is the object the mechanic is copying
            from. Both are real-world colours (theme.css), not brand tones. */}
        <div className={`relative flex rounded-[var(--fd-r-md)] overflow-hidden border border-line-strong shadow-card transition-shadow focus-within:border-gold focus-within:shadow-focus ${boltShot ? 'is-firing' : ''}`}>

          {/* GB identifier band */}
          <div className="bg-plate-band w-12 flex flex-col items-center justify-center gap-1 pointer-events-none">
            <div className="grid grid-cols-4 gap-0.5 w-5">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="w-1 h-1 bg-on-plate-band rounded-full"></div>
              ))}
            </div>
            <span className="text-on-fill font-bold text-xs tracking-tight">UK</span>
          </div>

          <div className="relative flex-grow min-w-0">
            <input
              type="text"
              name="vrm"
              id="vrm-lookup-input"
              className="block w-full text-xl sm:text-2xl h-14 bg-plate text-on-plate font-mono font-bold tracking-[0.2em] uppercase placeholder:text-on-plate/40 outline-none px-4 shadow-[inset_0_1px_0_var(--fd-sheen),inset_0_-2px_0_var(--fd-key-lip)]"
              placeholder="ENTER VRM"
              value={vrmInput}
              onChange={(e) => {
                setVrmInput(e.target.value.toUpperCase());
                setError(null);
                setDvlaData(null);
                setSource(null);
                emitPending(null, '', e.target.value.toUpperCase());
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // Not a <form>, so Enter has no default to suppress here —
                  // but the surrounding screen usually IS one, and letting the
                  // key bubble would submit it. See the note above the render.
                  e.preventDefault();
                  e.stopPropagation();
                  void handleLookup();
                }
              }}
              disabled={loading}
              autoComplete="off"
            />
            {loading && <span aria-hidden="true" className="fd-plate-scan" />}
          </div>

          <button
            type="button"
            // Not disabled while searching: a disabled metal key drops to the
            // grey well, and the steel should stay lit while the bolt flies.
            onClick={() => { if (!loading) void handleLookup(); }}
            disabled={!vrmInput.trim()}
            aria-busy={loading}
            // Steel: Lookup is everyday work, and the emerald belongs to the
            // screen's own confirm button below. Attached to the plate, so it
            // flashes ember on press but does not drop.
            className="fd-btn fd-btn-steel shrink-0 rounded-none border-0 border-l h-14 px-3 sm:px-6 text-xs sm:text-sm hover:transform-none active:transform-none"
          >
            {loading ? (
              <span className="animate-pulse flex items-center gap-2 font-semibold">SEARCHING...</span>
            ) : (
              <span className="flex items-center gap-2 font-bold tracking-wider"><ArrowLeft className="h-4 w-4 text-[var(--fd-bolt-ic)]" strokeWidth={2.5} /> LOOKUP</span>
            )}
          </button>
          {/* The gold bolt the Lookup key fires back across the plate. */}
          {boltShot > 0 && (
            <span key={boltShot} aria-hidden="true" className="fd-bolt">
              <span className="fd-bolt-trail" />
              <span className="fd-bolt-head"><ArrowLeft strokeWidth={3} /></span>
            </span>
          )}
        </div>
      </div>

      {/* 2. ERROR STATE */}
      {error && (
        <div className={`rounded-md p-4 animate-in fade-in slide-in-from-top-1 ${apiUnavailable ? 'bg-gold/5 border border-gold/30' : 'bg-danger/10 border border-danger/30'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className={`h-5 w-5 flex-shrink-0 ${apiUnavailable ? 'text-gold' : 'text-danger'}`} />
              <h3 className={`ml-3 text-sm font-medium ${apiUnavailable ? 'text-gold' : 'text-danger'}`}>{error}</h3>
            </div>
            {onSkipToManual && (
              <button
                type="button"
                onClick={() => onSkipToManual(vrmInput.trim().toUpperCase())}
                className="text-xs font-bold text-gold hover:text-gold underline underline-offset-2 transition-colors"
              >
                Skip → Enter Manually
              </button>
            )}
          </div>
        </div>
      )}

      {/* 3. DB HIT — Quick Source Badge */}
      {source === 'db' && !error && !dvlaData && (
        <div className="rounded-md bg-success/5 border border-success/30 p-3 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
          <span className="text-sm font-medium text-success">Vehicle found in database — loaded instantly.</span>
        </div>
      )}

      {/* 4. POST-DVLA-LOOKUP STATE (Data Summary + Model Input Gap) */}
      {dvlaData && !error && (
        <div className="bg-card border border-line rounded-lg p-5 mt-4 shadow-xl ring-1 ring-success/20 animate-in zoom-in-95 duration-200">

          {/* Source indicator */}
          <div className="mb-3">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-info bg-info/10 px-2 py-0.5 rounded border border-info/20">
              DVLA Lookup
            </span>
          </div>

          {/* Vehicle Metadata Summary */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h3 className="text-xl font-bold text-fg uppercase tracking-wider">
                {dvlaData.make || 'Unknown Make'}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-fg-3">
                <span className="font-semibold text-fg-2">{dvlaData.yearOfManufacture}</span>
                <span>&bull;</span>
                <span>{dvlaData.engineCapacity ? `${dvlaData.engineCapacity}cc` : 'Unknown cc'}</span>
                <span>&bull;</span>
                <span>{dvlaData.colour || 'Unknown Colour'}</span>
                <span>&bull;</span>
                <span className="uppercase">{dvlaData.fuelType || 'Unknown Fuel'}</span>
              </div>
            </div>

            {/* Dynamic Status Badges */}
            <div className="flex flex-col gap-2 shrink-0">
              {dvlaData.motStatus && (() => {
                const ok = motIsValid(dvlaData.motStatus, dvlaData.motExpiryDate);
                return (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase rounded border ${
                    ok ? 'bg-success/10 text-success border-success/20' : 'bg-danger/10 text-danger border-danger/20'
                  }`}>
                    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    MOT: {ok || dvlaData.motStatus.toLowerCase() !== 'valid' ? dvlaData.motStatus : 'Expired'}
                  </span>
                );
              })()}
              {dvlaData.taxStatus && (() => {
                const ok = taxIsValid(dvlaData.taxStatus, dvlaData.taxDueDate);
                return (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase rounded border ${
                    ok ? 'bg-success/10 text-success border-success/20' : 'bg-danger/10 text-danger border-danger/20'
                  }`}>
                    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    TAX: {ok || dvlaData.taxStatus.toLowerCase() !== 'taxed' ? dvlaData.taxStatus : 'Expired'}
                  </span>
                );
              })()}
            </div>
          </div>

          {/* What DVSA adds that DVLA cannot: the vehicle's history. Kept to one
              scannable strip here — the full test-by-test record lives on the
              vehicle's page, not in the middle of a create flow. */}
          {dvlaData.motHistory && (
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-fg-3">
              {dvlaData.motHistory.lastTestDate && (
                <span>
                  Last MOT{' '}
                  <span className="font-semibold text-fg">
                    {new Date(dvlaData.motHistory.lastTestDate).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </span>
                  {dvlaData.motHistory.lastTestResult && (
                    <span className={dvlaData.motHistory.lastTestResult === 'PASSED'
                      ? ' text-success' : ' text-danger'}>
                      {' '}({dvlaData.motHistory.lastTestResult.toLowerCase()})
                    </span>
                  )}
                </span>
              )}
              {dvlaData.motHistory.lastOdometerValue && (
                <>
                  <span aria-hidden="true">&bull;</span>
                  <span>
                    <span className="font-semibold text-fg">
                      {Number(dvlaData.motHistory.lastOdometerValue).toLocaleString('en-GB')}
                      {dvlaData.motHistory.lastOdometerUnit === 'KM' ? ' km' : ' mi'}
                    </span>{' '}
                    at that test
                  </span>
                </>
              )}
              {dvlaData.motHistory.advisoryCount > 0 && (
                <>
                  <span aria-hidden="true">&bull;</span>
                  <span className="text-gold font-semibold">
                    {dvlaData.motHistory.advisoryCount} advisor{dvlaData.motHistory.advisoryCount === 1 ? 'y' : 'ies'}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Only ever shown for a definite 'Yes'. 'Unknown' means DVSA holds no
              recall data — saying anything reassuring about it would be a
              safety claim we cannot stand behind. */}
          {dvlaData.motHistory?.hasOutstandingRecall === 'Yes' && (
            <div className="mt-3 rounded-md bg-danger/10 border border-danger/30 p-2.5 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-danger shrink-0 mt-0.5" />
              <span className="text-xs font-semibold text-danger">
                This vehicle has an outstanding manufacturer recall.
              </span>
            </div>
          )}

          {/* DVSA was asked but did not answer. Say so, rather than letting an
              empty history read as "this vehicle has never been tested". */}
          {!dvlaData.motHistory && dvlaData.sources?.mot && dvlaData.sources.mot !== 'ok' && (
            <p className="mt-3 text-xs text-fg-4">
              Vehicle found — MOT history is temporarily unavailable.
            </p>
          )}

          <hr className="border-line my-5" />

          {/* Fill The 'Model' Gap + Mileage hint */}
          <div className="space-y-4">
            <div>
              <label htmlFor="model-input" className="flex items-center text-sm font-semibold text-success mb-1">
                Vehicle Model
                <span className="ml-1 text-danger" title="Required">*</span>
              </label>
              <input
                type="text"
                id="model-input"
                className="block w-full rounded-md border-line-strong bg-canvas text-fg focus:ring-success focus:border-success sm:text-sm py-2.5 px-3 shadow-inner placeholder-fg-4"
                placeholder="e.g. Focus, M3, Fiesta..."
                value={modelInput}
                onChange={(e) => {
                  setModelInput(e.target.value);
                  emitPending(dvlaData, e.target.value, vrmInput);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleConfirmNewVehicle();
                  }
                }}
                autoFocus
              />
              <p className="mt-1.5 text-xs text-fg-4">
                {dvlaData.model
                  ? 'Filled in from the DVSA MOT record — edit it if it is not right.'
                  : 'The DVLA does not provide specific model names. Fill this in to finalise the record.'}
              </p>
            </div>

            <div className="p-2.5 bg-info/5 border border-info/10 rounded-md">
              <p className="text-xs text-info/80">
                {mileageHint && dvlaData.motHistory?.lastOdometerValue ? mileageHint : dvlaData.motHistory?.lastOdometerValue && dvlaData.motHistory.lastOdometerUnit !== 'KM' ? (
                  <>💡 <strong>Tip:</strong> After confirming, the mileage box offers the MOT reading above in one tap — take it, or put today's in instead.</>
                ) : (
                  <>💡 <strong>Tip:</strong> Add mileage after confirming for better service tracking.</>
                )}
              </p>
            </div>

            <button
              type="button"
              onClick={handleConfirmNewVehicle}
              className="fd-btn fd-btn-go fd-btn-lg w-full"
            >
              {confirmButtonLabel}
            </button>
          </div>

        </div>
      )}
    </div>
  );
}
